<template>
  <div class="min-h-screen bg-gray-50">
    <!-- Header -->
    <header class="bg-white shadow-sm border-b border-gray-200">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex justify-between items-center py-4">
          <div class="flex items-center gap-3">
            <div
              class="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center"
            >
              <span class="text-white font-bold text-lg">R</span>
            </div>
            <div>
              <h1 class="text-xl font-bold text-gray-900">Admin Dashboard</h1>
              <p class="text-sm text-gray-500">
                Gestion des cartes personnalisées
              </p>
            </div>
          </div>
          <div class="flex items-center gap-4">
            <router-link
              to="/admin/add"
              class="px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors font-medium text-sm flex items-center gap-2"
            >
              <svg
                class="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 4v16m8-8H4"
                />
              </svg>
              Ajouter une carte
            </router-link>
            <button
              @click="logout"
              class="px-4 py-2 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors font-medium text-sm"
            >
              Déconnexion
            </button>
          </div>
        </div>
      </div>
    </header>

    <!-- Main Content -->
    <main class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <!-- Loading State -->
      <div v-if="loading" class="text-center py-20">
        <div
          class="inline-block w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"
        ></div>
        <p class="mt-4 text-gray-600">Chargement des cartes...</p>
      </div>

      <!-- Error State -->
      <div
        v-else-if="error"
        class="bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-2xl text-center max-w-2xl mx-auto"
      >
        <p class="font-medium">{{ error }}</p>
        <button
          @click="fetchCards"
          class="mt-4 px-4 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors"
        >
          Réessayer
        </button>
      </div>

      <!-- Cards List -->
      <div v-else-if="cards.length > 0">
        <div
          class="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden"
        >
          <!-- Table Header -->
          <div
            class="grid grid-cols-12 gap-4 px-6 py-4 bg-gray-50 border-b border-gray-200 font-semibold text-sm text-gray-700"
          >
            <div class="col-span-4">Carte</div>
            <div class="col-span-2">Catégorie</div>
            <div class="col-span-2">Prix</div>
            <div class="col-span-2">Stock</div>
            <div class="col-span-2">Actions</div>
          </div>

          <!-- Table Body -->
          <div class="divide-y divide-gray-200">
            <div
              v-for="card in cards"
              :key="card.id"
              class="grid grid-cols-12 gap-4 px-6 py-4 items-center hover:bg-gray-50 transition-colors"
            >
              <!-- Card Info -->
              <div class="col-span-4">
                <div class="flex items-center gap-3">
                  <div
                    v-if="card.imageUrl"
                    class="w-12 h-12 rounded-xl overflow-hidden bg-gray-100 flex-shrink-0"
                  >
                    <img
                      :src="card.imageUrl"
                      :alt="card.title"
                      class="w-full h-full object-cover"
                    />
                  </div>
                  <div
                    v-else
                    class="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center flex-shrink-0"
                  >
                    <svg
                      class="w-6 h-6 text-blue-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                      />
                    </svg>
                  </div>
                  <div class="min-w-0">
                    <p class="font-medium text-gray-900 truncate">
                      {{ card.title }}
                    </p>
                    <p
                      v-if="card.description"
                      class="text-sm text-gray-500 truncate"
                    >
                      {{ card.description }}
                    </p>
                    <p class="text-xs text-gray-400 mt-1">
                      {{ formatDate(card.createdAt) }}
                    </p>
                  </div>
                </div>
              </div>

              <!-- Category -->
              <div class="col-span-2">
                <span
                  class="inline-flex px-3 py-1 rounded-full text-xs font-medium"
                  :class="getCategoryClass(card.category)"
                >
                  {{ card.category }}
                </span>
              </div>

              <!-- Price -->
              <div class="col-span-2">
                <span class="font-semibold text-gray-900">{{
                  formatPrice(card.price, card.currency)
                }}</span>
              </div>

              <!-- Stock -->
              <div class="col-span-2">
                <div class="flex items-center gap-2">
                  <span
                    class="font-medium"
                    :class="
                      card.stockQuantity > 0 ? 'text-green-600' : 'text-red-600'
                    "
                  >
                    {{ card.stockQuantity }}
                  </span>
                  <span class="text-gray-500 text-sm">en stock</span>
                </div>
              </div>

              <!-- Actions -->
              <div class="col-span-2">
                <div class="flex items-center gap-2">
                  <button
                    @click="editCard(card)"
                    class="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title="Modifier"
                  >
                    <svg
                      class="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                      />
                    </svg>
                  </button>
                  <button
                    @click="confirmDelete(card)"
                    class="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Supprimer"
                  >
                    <svg
                      class="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Stats -->
        <div class="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div
            class="bg-white rounded-2xl shadow-sm border border-gray-200 p-6"
          >
            <div class="flex items-center gap-4">
              <div
                class="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center"
              >
                <svg
                  class="w-6 h-6 text-blue-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                  />
                </svg>
              </div>
              <div>
                <p class="text-sm text-gray-500">Total cartes</p>
                <p class="text-2xl font-bold text-gray-900">
                  {{ cards.length }}
                </p>
              </div>
            </div>
          </div>
          <div
            class="bg-white rounded-2xl shadow-sm border border-gray-200 p-6"
          >
            <div class="flex items-center gap-4">
              <div
                class="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center"
              >
                <svg
                  class="w-6 h-6 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <div>
                <p class="text-sm text-gray-500">En stock</p>
                <p class="text-2xl font-bold text-gray-900">{{ totalStock }}</p>
              </div>
            </div>
          </div>
          <div
            class="bg-white rounded-2xl shadow-sm border border-gray-200 p-6"
          >
            <div class="flex items-center gap-4">
              <div
                class="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center"
              >
                <svg
                  class="w-6 h-6 text-red-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <div>
                <p class="text-sm text-gray-500">Rupture de stock</p>
                <p class="text-2xl font-bold text-gray-900">{{ outOfStock }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div v-else class="text-center py-20">
        <div
          class="inline-flex items-center justify-center w-20 h-20 bg-gray-100 rounded-full mb-6"
        >
          <svg
            class="w-10 h-10 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
            />
          </svg>
        </div>
        <h3 class="text-xl font-bold text-gray-900 mb-2">
          Aucune carte personnalisée
        </h3>
        <p class="text-gray-500 mb-6">
          Commencez par ajouter votre première carte personnalisée.
        </p>
        <router-link
          to="/admin/add"
          class="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors font-medium"
        >
          <svg
            class="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 4v16m8-8H4"
            />
          </svg>
          Ajouter une carte
        </router-link>
      </div>
    </main>

    <!-- Delete Confirmation Modal -->
    <div
      v-if="showDeleteModal"
      class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
    >
      <div class="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
        <h3 class="text-xl font-bold text-gray-900 mb-2">
          Confirmer la suppression
        </h3>
        <p class="text-gray-600 mb-6">
          Êtes-vous sûr de vouloir supprimer la carte "<strong>{{
            cardToDelete?.title
          }}</strong
          >" ? Cette action est irréversible.
        </p>
        <div class="flex gap-3">
          <button
            @click="showDeleteModal = false"
            class="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors font-medium"
          >
            Annuler
          </button>
          <button
            @click="deleteCard"
            :disabled="deleting"
            class="flex-1 px-4 py-3 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors font-medium disabled:opacity-50"
          >
            <span v-if="deleting">Suppression...</span>
            <span v-else>Supprimer</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, getCurrentInstance } from 'vue';
import { useRouter } from 'vue-router';

const router = useRouter();
const { proxy } = getCurrentInstance();
const { $constants } = proxy;

const cards = ref([]);
const loading = ref(true);
const error = ref('');
const showDeleteModal = ref(false);
const cardToDelete = ref(null);
const deleting = ref(false);

const totalStock = computed(() =>
  cards.value.reduce((sum, card) => sum + (card.stockQuantity || 0), 0)
);
const outOfStock = computed(
  () => cards.value.filter((card) => (card.stockQuantity || 0) === 0).length
);

onMounted(() => {
  // Check authentication
  const token = localStorage.getItem('adminToken');
  if (!token) {
    router.push('/admin/login');
    return;
  }
  fetchCards();
});

const fetchCards = async () => {
  loading.value = true;
  error.value = '';

  try {
    const response = await fetch(`${$constants.ENDPOINTS.LOCAL_API}/custom-cards`);
    const data = await response.json();

    if (response.ok) {
      // API returns { success: true, data: [...], count: N }
      cards.value = data.data || data.cards || [];
    } else {
      error.value = 'Erreur lors du chargement des cartes';
    }
  } catch (err) {
    error.value =
      'Erreur de connexion au serveur. Vérifiez que le serveur API est démarré.';
    console.error('Fetch cards error:', err);
  } finally {
    loading.value = false;
  }
};

const confirmDelete = (card) => {
  cardToDelete.value = card;
  showDeleteModal.value = true;
};

const deleteCard = async () => {
  if (!cardToDelete.value) return;

  deleting.value = true;

  try {
    const token = localStorage.getItem('adminToken');
    const response = await fetch(
      `${$constants.ENDPOINTS.LOCAL_API}/custom-cards/${cardToDelete.value.id}`,
      {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );

    if (response.ok) {
      cards.value = cards.value.filter((c) => c.id !== cardToDelete.value.id);
      showDeleteModal.value = false;
      cardToDelete.value = null;
    } else {
      alert('Erreur lors de la suppression de la carte');
    }
  } catch (err) {
    alert('Erreur de connexion au serveur');
    console.error('Delete card error:', err);
  } finally {
    deleting.value = false;
  }
};

const editCard = (card) => {
  router.push(`/admin/edit/${card.id}`);
};

const logout = () => {
  localStorage.removeItem('adminToken');
  router.push('/admin/login');
};

const formatPrice = (price, currency = 'XOF') => {
  if (!price) return '0';
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0
  }).format(price);
};

const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  }).format(date);
};

const getCategoryClass = (category) => {
  const classes = {
    'Payment Cards': 'bg-purple-100 text-purple-700',
    Gaming: 'bg-blue-100 text-blue-700',
    Shopping: 'bg-pink-100 text-pink-700',
    Streaming: 'bg-orange-100 text-orange-700'
  };
  return classes[category] || 'bg-gray-100 text-gray-700';
};
</script>
